import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import MDSpinner from "react-md-spinner";

import {
  fetchGroupFarms,
  fetchGroupLandPlots,
} from "../PendingSchedules/serviceOrderLocationApi";
import FarmRainHeatmapMap from "./FarmRainHeatmapMap";
import { clampSize, useDragResize } from "./useDragResize";
import {
  averageGaugeRainMm,
  buildGaugePositionsFromRing,
  findSanRafaelSubFarm,
  formatRainMm,
  sumGaugeRainMm,
} from "../../utils/rainHeatmapUtils";
import {
  computeRingCentroid,
  parseBoundaryGeoJsonRing,
} from "../../utils/mapMeasurements";
import {
  MAP_VIEWPORT_SCOPES,
  readLocalMapLayout,
  writeLocalMapLayout,
} from "../../utils/mapViewport";

import "./FarmMapPluviometry.css";

const PERIOD_OPTIONS = [
  { id: "today", label: "Hoje" },
  { id: "week", label: "Última semana" },
  { id: "month", label: "Último mês" },
];

const DEFAULT_CONTROLS_WIDTH = 320;
const DEFAULT_MAP_HEIGHT = 560;

function readRainMapLayout(farmId) {
  const maxControlsWidth = Math.min(480, window.innerWidth * 0.42);
  const maxMapHeight = Math.min(920, window.innerHeight * 0.78);
  const saved = readLocalMapLayout(MAP_VIEWPORT_SCOPES.FARM_RAIN, farmId);

  const savedControlsWidth =
    saved && saved.listWidth != null ? saved.listWidth : DEFAULT_CONTROLS_WIDTH;
  const savedMapHeight =
    saved && saved.mapHeight != null ? saved.mapHeight : DEFAULT_MAP_HEIGHT;

  return {
    controlsWidth: clampSize(savedControlsWidth, 260, maxControlsWidth),
    mapHeight: clampSize(savedMapHeight, 360, maxMapHeight),
  };
}

function FarmMapPluviometryDemo({ farmId }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [targetFarmName, setTargetFarmName] = useState("");
  const [boundaryRing, setBoundaryRing] = useState([]);
  const [landPlots, setLandPlots] = useState([]);
  const [gauges, setGauges] = useState([]);
  const [period, setPeriod] = useState("today");
  const [loadError, setLoadError] = useState("");
  const [controlsWidth, setControlsWidth] = useState(
    () => readRainMapLayout(farmId).controlsWidth
  );
  const [mapHeight, setMapHeight] = useState(
    () => readRainMapLayout(farmId).mapHeight
  );
  const controlsWidthRef = useRef(controlsWidth);
  const mapHeightRef = useRef(mapHeight);

  useEffect(() => {
    controlsWidthRef.current = controlsWidth;
  }, [controlsWidth]);

  useEffect(() => {
    mapHeightRef.current = mapHeight;
  }, [mapHeight]);

  useEffect(() => {
    const layout = readRainMapLayout(farmId);
    controlsWidthRef.current = layout.controlsWidth;
    mapHeightRef.current = layout.mapHeight;
    setControlsWidth(layout.controlsWidth);
    setMapHeight(layout.mapHeight);
  }, [farmId]);

  const handleLayoutResize = useCallback((axis, dx, dy) => {
    const maxControlsWidth = Math.min(480, window.innerWidth * 0.42);
    const maxMapHeight = Math.min(920, window.innerHeight * 0.78);

    if (axis === "controls") {
      setControlsWidth((current) => {
        const next = clampSize(current + dx, 260, maxControlsWidth);
        controlsWidthRef.current = next;
        return next;
      });
    }
    if (axis === "map-e") {
      setControlsWidth((current) => {
        const next = clampSize(current - dx, 260, maxControlsWidth);
        controlsWidthRef.current = next;
        return next;
      });
    }
    if (axis === "map-s" || axis === "map-se") {
      setMapHeight((current) => {
        const next = clampSize(current + dy, 360, maxMapHeight);
        mapHeightRef.current = next;
        return next;
      });
    }
    if (axis === "map-se") {
      setControlsWidth((current) => {
        const next = clampSize(current - dx, 260, maxControlsWidth);
        controlsWidthRef.current = next;
        return next;
      });
    }
  }, []);

  const persistRainMapLayout = useCallback(() => {
    if (!farmId) return;
    writeLocalMapLayout(MAP_VIEWPORT_SCOPES.FARM_RAIN, farmId, {
      listWidth: controlsWidthRef.current,
      mapHeight: mapHeightRef.current,
    });
  }, [farmId]);

  const { onResizeStart } = useDragResize(handleLayoutResize, persistRainMapLayout);

  useEffect(() => {
    let cancelled = false;

    async function loadDemoContext() {
      setLoading(true);
      setLoadError("");
      try {
        const [groupFarms, plots] = await Promise.all([
          fetchGroupFarms(farmId),
          fetchGroupLandPlots(farmId),
        ]);
        if (cancelled) return;

        const subFarm = findSanRafaelSubFarm(groupFarms);
        const ring = parseBoundaryGeoJsonRing(
          subFarm && subFarm.boundary_geojson
        );
        const nextGauges = buildGaugePositionsFromRing(ring);

        if (!subFarm || ring.length < 3 || nextGauges.length < 5) {
          setLoadError(
            t(
              "Não foi possível carregar o perímetro da sub-fazenda San Rafael. Desenhe o contorno no mapa da fazenda para usar o demonstrativo."
            )
          );
          setBoundaryRing([]);
          setGauges([]);
          setLandPlots(plots);
          setTargetFarmName("San Rafael");
          return;
        }

        setTargetFarmName(subFarm.nome_da_fazenda || "San Rafael");
        setBoundaryRing(ring);
        setGauges(nextGauges);
        setLandPlots(plots);
      } catch (error) {
        if (!cancelled) {
          setLoadError(
            t("Não foi possível carregar os dados do mapa de chuva.")
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadDemoContext();
    return () => {
      cancelled = true;
    };
  }, [farmId, t]);

  const mapViewport = useMemo(() => {
    const centroid = computeRingCentroid(boundaryRing);
    if (!centroid) {
      return { center: null, zoom: 14 };
    }
    return { center: centroid, zoom: 15 };
  }, [boundaryRing]);

  function handleGaugeChange(gaugeId, value) {
    const mm = Math.max(0, Math.min(120, Number(value) || 0));
    setGauges((current) =>
      current.map((gauge) =>
        gauge.id === gaugeId ? { ...gauge, mm } : gauge
      )
    );
  }

  const totalMm = sumGaugeRainMm(gauges);
  const averageMm = averageGaugeRainMm(gauges);

  return (
    <div className="farm-map-pluviometry">
      <div className="farm-map-pluviometry__intro">
        <div>
          <h2>{t("Mapa de calor — demonstração")}</h2>
          <p>
            {t(
              "Exemplo com 5 pluviômetros na sub-fazenda {{name}}. Ajuste os valores em mm para ver como a chuva se distribui no mapa.",
              { name: targetFarmName || "San Rafael" }
            )}
          </p>
        </div>
        <div className="farm-map-pluviometry__periods">
          {PERIOD_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={
                period === option.id
                  ? "btn btn-primary btn-sm"
                  : "btn btn-default btn-sm"
              }
              onClick={() => setPeriod(option.id)}
            >
              {t(option.label)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="farm-map-pluviometry__loading">
          <MDSpinner singleColor="#1ABB9C" size={36} />
        </div>
      ) : loadError ? (
        <div className="farm-map-pluviometry__empty">
          <strong>{loadError}</strong>
        </div>
      ) : (
        <div
          className="farm-map-pluviometry__layout"
          style={{ "--controls-width": `${controlsWidth}px` }}
        >
          <aside className="farm-map-pluviometry__controls">
            <div className="farm-map-pluviometry__summary">
              <div>
                <span>{t("Período")}</span>
                <strong>
                  {t(
                    PERIOD_OPTIONS.find((item) => item.id === period).label
                  )}
                </strong>
              </div>
              <div>
                <span>{t("Média")}</span>
                <strong>{formatRainMm(averageMm)} mm</strong>
              </div>
              <div>
                <span>{t("Total (5 pontos)")}</span>
                <strong>{formatRainMm(totalMm)} mm</strong>
              </div>
            </div>

            <p className="farm-map-pluviometry__hint">
              {t(
                "Regra sugerida: em talhões grandes, use ao menos 5 pontos (cantos + centro) para um mapa de chuva confiável."
              )}
            </p>

            <ul className="farm-map-pluviometry__gauge-list">
              {gauges.map((gauge) => (
                <li key={gauge.id}>
                  <label htmlFor={`gauge-${gauge.id}`}>{gauge.label}</label>
                  <div className="farm-map-pluviometry__gauge-input">
                    <input
                      id={`gauge-${gauge.id}`}
                      type="range"
                      min="0"
                      max="80"
                      step="0.5"
                      value={gauge.mm}
                      onChange={(event) =>
                        handleGaugeChange(gauge.id, event.target.value)
                      }
                    />
                    <input
                      type="number"
                      min="0"
                      max="120"
                      step="0.5"
                      value={gauge.mm}
                      onChange={(event) =>
                        handleGaugeChange(gauge.id, event.target.value)
                      }
                    />
                    <span>mm</span>
                  </div>
                </li>
              ))}
            </ul>
          </aside>

          <div
            className="farm-map-pluviometry__layout-resize"
            title={t("Arraste para redimensionar o mapa")}
            onMouseDown={(event) => onResizeStart("controls", event)}
          />

          <div
            className="farm-map-pluviometry__map-wrap"
            style={{ height: mapHeight }}
          >
            <FarmRainHeatmapMap
              boundaryRing={boundaryRing}
              landPlots={landPlots}
              gauges={gauges}
              center={mapViewport.center}
              zoom={mapViewport.zoom}
              mapHeight={mapHeight}
            />
            <div
              className="farm-map-page__map-resize farm-map-page__map-resize--e"
              title={t("Arraste para ampliar o mapa")}
              onMouseDown={(event) => onResizeStart("map-e", event)}
            />
            <div
              className="farm-map-page__map-resize farm-map-page__map-resize--s"
              title={t("Arraste para ampliar o mapa")}
              onMouseDown={(event) => onResizeStart("map-s", event)}
            />
            <div
              className="farm-map-page__map-resize farm-map-page__map-resize--se"
              title={t("Arraste para ampliar o mapa")}
              onMouseDown={(event) => onResizeStart("map-se", event)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default FarmMapPluviometryDemo;
